import { Col, Row } from 'antd';
import { PrimaryButton, SecondaryButton } from 'common-components/buttons';
import SpinningLoadingActionModel from 'common-components/loading/SpinningLoadingActionModel';
import ActionModal from 'common-components/modal/ActionModal';
import { Paragraph } from 'common-components/typography';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { dispatch, IRootDispatch } from 'stores/rematch/root-store';
import { useGroupScheduleProgress } from 'stores/hooks/query-hooks/use-group-schedule-progress';
import { notifications } from '@good/ui/notifications';
import ScheduleListItem from './ScheduleListItem';

interface IDeleteScheduleModalProps {
  onClose: () => void;
  doRemoveGroupServiceSchedule: typeof dispatch.servicesStore.doRemoveGroupServiceSchedule;
  doFetchGroupServiceOverview: typeof dispatch.groupServiceStore.doFetchGroupServiceOverview;
  doFetchGroupServiceDetail: typeof dispatch.servicesStore.doFetchGroupServiceDetail;
  isOpen: boolean;
  serviceId: string;
  serviceScheduleId: string;
  resetScheduleList: () => void;
  scheduleName: string;
  schedulePeriodText: string;
  displayTimeSlots: string[];
}

const DeleteScheduleModal: React.FC<IDeleteScheduleModalProps> = ({
  onClose,
  doRemoveGroupServiceSchedule,
  doFetchGroupServiceOverview,
  doFetchGroupServiceDetail,
  isOpen,
  serviceId,
  serviceScheduleId,
  resetScheduleList,
  scheduleName,
  schedulePeriodText,
  displayTimeSlots,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { startProgressTracking } = useGroupScheduleProgress({
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'Schedule removed successfully.',
        color: 'green',
        autoClose: 3000,
        withCloseButton: false,
      });
      onClose();
      resetScheduleList();

      // Refresh the group service data when the job completes
      doFetchGroupServiceOverview({ serviceId });
      doFetchGroupServiceDetail({ serviceId });
    },
    onError: (error) => {
      notifications.show({
        title: 'Error',
        message: `Failed to remove schedule: ${error}`,
        color: 'red',
        autoClose: 3000,
        withCloseButton: false,
      });
    },
  });

  const deleteSchedule = async () => {
    try {
      setIsLoading(true);
      const result = await doRemoveGroupServiceSchedule({ serviceId, serviceScheduleId });

      // Check if this is a queued operation (202 response)
      if (result?.jobId) {
        startProgressTracking(result.jobId, `Deleting schedule "${scheduleName}"... This may take a few moments.`);
        onClose(); // Close modal immediately for queued operations
      } else {
        // Immediate success
        notifications.show({
          title: 'Success',
          message: 'Schedule removed successfully.',
          color: 'green',
          autoClose: 3000,
          withCloseButton: false,
        });
        onClose();
        resetScheduleList();
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Oops, an error has occurred, please try again.',
        color: 'red',
        autoClose: 3000,
        withCloseButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <ActionModal title='Delete schedule' isOpen={isOpen} onClose={onClose} width='medium'>
        <div className='mv-large'>
          <Paragraph className='mb-medium'>
            Are you sure you want to delete the schedule <strong>{scheduleName}</strong>?
          </Paragraph>
          <Paragraph className='mb-medium'>
            This will remove all sessions for the period <strong>{schedulePeriodText}</strong>.
          </Paragraph>
          <Paragraph className='mb-medium'>
            <strong>Time slots:</strong>
          </Paragraph>
          <Row gutter={[8, 8]}>
            {displayTimeSlots.map((timeSlot, index) => (
              <Col key={index} span={24}>
                <Paragraph className='mb-none text-color-secondary'>• {timeSlot}</Paragraph>
              </Col>
            ))}
          </Row>
          <Paragraph className='mb-large text-color-secondary'>This action cannot be undone.</Paragraph>
        </div>
        <div className='flex-row justify-end'>
          <SecondaryButton size='large' onClick={onClose} className='mr-medium'>
            Cancel
          </SecondaryButton>
          <PrimaryButton size='large' color='red' onClick={deleteSchedule} loading={isLoading} disabled={isLoading}>
            Delete Schedule
          </PrimaryButton>
        </div>
      </ActionModal>
    </div>
  );
};

const mapDispatch = (dispatch: IRootDispatch) => ({
  doRemoveGroupServiceSchedule: dispatch.servicesStore.doRemoveGroupServiceSchedule,
  doFetchGroupServiceOverview: dispatch.groupServiceStore.doFetchGroupServiceOverview,
  doFetchGroupServiceDetail: dispatch.servicesStore.doFetchGroupServiceDetail,
});

export default connect(null, mapDispatch)(DeleteScheduleModal);
